import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { type ContactFilters, ghlApi } from "./ghl"
import type { Decision, GhlDnd, GhlMessageType, GhlOpportunityStatus } from "./ghl-types"

// Hooks for Marketing Supercharge (mock-only, see ghl.ts). Every write
// invalidates the whole `ghl` tree — the lists, the record and the
// analytics all read the same stored objects.

const K = {
  all: ["ghl"] as const,
  users: (t: string) => ["ghl", "users", t] as const,
  contacts: (t: string, f: ContactFilters) => ["ghl", "contacts", t, f] as const,
  contact: (t: string, id: string) => ["ghl", "contact", t, id] as const,
  conversations: (t: string, f: object) => ["ghl", "conversations", t, f] as const,
  messages: (t: string, id: string) => ["ghl", "messages", t, id] as const,
  pipelines: (t: string) => ["ghl", "pipelines", t] as const,
  opportunities: (t: string, f: object) => ["ghl", "opportunities", t, f] as const,
  tasks: (t: string, f: object) => ["ghl", "tasks", t, f] as const,
  appointments: (t: string) => ["ghl", "appointments", t] as const,
  analytics: (t: string, days: number) => ["ghl", "analytics", t, days] as const,
  contactsRich: (t: string, f: ContactFilters) => ["ghl", "contacts-rich", t, f] as const,
  conversationsRich: (t: string, f: object) => ["ghl", "conversations-rich", t, f] as const,
  queue: (t: string) => ["ghl", "queue", t] as const,
}

type TeamId = number | string | null | undefined
const key = (t: TeamId) => String(t ?? "none")

export function useGhlUsers(teamId: TeamId) {
  return useQuery({ queryKey: K.users(key(teamId)), queryFn: () => ghlApi.users(teamId!), enabled: teamId != null, staleTime: 5 * 60_000 })
}
export function useGhlContacts(teamId: TeamId, f: ContactFilters = {}) {
  return useQuery({ queryKey: K.contacts(key(teamId), f), queryFn: () => ghlApi.contacts(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev })
}
export function useGhlContact(teamId: TeamId, contactId: string | undefined) {
  return useQuery({ queryKey: K.contact(key(teamId), contactId ?? ""), queryFn: () => ghlApi.contact(teamId!, contactId!), enabled: teamId != null && !!contactId })
}
export function useGhlConversations(teamId: TeamId, f: { unread?: boolean; assignedTo?: string; starred?: boolean } = {}) {
  return useQuery({ queryKey: K.conversations(key(teamId), f), queryFn: () => ghlApi.conversations(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev, refetchInterval: 15_000 })
}
export function useGhlMessages(teamId: TeamId, conversationId: string | null | undefined) {
  return useQuery({ queryKey: K.messages(key(teamId), conversationId ?? ""), queryFn: () => ghlApi.messages(teamId!, conversationId!), enabled: teamId != null && !!conversationId, refetchInterval: 5_000 })
}
export function useGhlPipelines(teamId: TeamId) {
  return useQuery({ queryKey: K.pipelines(key(teamId)), queryFn: () => ghlApi.pipelines(teamId!), enabled: teamId != null, staleTime: 5 * 60_000 })
}
export function useGhlOpportunities(teamId: TeamId, f: { status?: GhlOpportunityStatus; stage?: string; assignedTo?: string } = {}) {
  return useQuery({ queryKey: K.opportunities(key(teamId), f), queryFn: () => ghlApi.opportunities(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev })
}
export function useGhlTasks(teamId: TeamId, f: { assignedTo?: string; completed?: boolean } = {}) {
  return useQuery({ queryKey: K.tasks(key(teamId), f), queryFn: () => ghlApi.tasks(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev })
}
export function useGhlAppointments(teamId: TeamId) {
  return useQuery({ queryKey: K.appointments(key(teamId)), queryFn: () => ghlApi.appointments(teamId!), enabled: teamId != null })
}
export function useGhlAnalytics(teamId: TeamId, days: number) {
  return useQuery({ queryKey: K.analytics(key(teamId), days), queryFn: () => ghlApi.analytics(teamId!, days), enabled: teamId != null, placeholderData: (prev) => prev })
}

function useInvalidateGhl() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: K.all })
}

export function usePatchContact(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({
    mutationFn: (v: { contactId: string; tags?: string[]; assignedTo?: string | null; dndSettings?: GhlDnd }) =>
      ghlApi.patchContact(teamId!, v.contactId, { tags: v.tags, assignedTo: v.assignedTo, dndSettings: v.dndSettings }),
    onSuccess: inv,
  })
}
export function useBulkContacts(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (v: { ids: string[]; addTag?: string; removeTag?: string; assignedTo?: string | null }) => ghlApi.bulkContacts(teamId!, v), onSuccess: inv })
}
export function useSendGhlMessage(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({
    mutationFn: (v: { conversationId: string; type: GhlMessageType; body: string; subject?: string | null }) =>
      ghlApi.sendMessage(teamId!, v.conversationId, { type: v.type, body: v.body, subject: v.subject }),
    // The delivery receipt lands a moment after the send — read again for it.
    onSuccess: () => { void inv(); window.setTimeout(() => { void inv() }, 2500) },
  })
}
export function usePatchOpportunity(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({
    mutationFn: (v: { id: string; pipelineStageId?: string; status?: GhlOpportunityStatus; monetaryValue?: number; assignedTo?: string | null }) =>
      ghlApi.patchOpportunity(teamId!, v.id, { pipelineStageId: v.pipelineStageId, status: v.status, monetaryValue: v.monetaryValue, assignedTo: v.assignedTo }),
    onSuccess: inv,
  })
}
export function useCreateTask(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (v: { contactId: string; title: string; body?: string | null; dueDate: string; assignedTo?: string | null }) => ghlApi.createTask(teamId!, v), onSuccess: inv })
}
export function usePatchTask(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (v: { id: string; completed?: boolean; title?: string; dueDate?: string; assignedTo?: string | null }) => ghlApi.patchTask(teamId!, v.id, v), onSuccess: inv })
}
export function useCreateNote(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (v: { contactId: string; body: string }) => ghlApi.createNote(teamId!, v), onSuccess: inv })
}

export function useGhlContactsRich(teamId: TeamId, f: ContactFilters = {}) {
  return useQuery({ queryKey: K.contactsRich(key(teamId), f), queryFn: () => ghlApi.contactsRich(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev })
}
export function useGhlConversationsRich(teamId: TeamId, f: { unread?: boolean; assignedTo?: string; starred?: boolean } = {}) {
  return useQuery({ queryKey: K.conversationsRich(key(teamId), f), queryFn: () => ghlApi.conversationsRich(teamId!, f), enabled: teamId != null, placeholderData: (prev) => prev, refetchInterval: 15_000 })
}
export function useGhlQueue(teamId: TeamId) {
  return useQuery({ queryKey: K.queue(key(teamId)), queryFn: () => ghlApi.queue(teamId!), enabled: teamId != null, placeholderData: (prev) => prev, refetchInterval: 20_000 })
}
export function useDecide(teamId: TeamId) {
  const inv = useInvalidateGhl()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (d: Decision) => ghlApi.decide(teamId!, d),
    onSuccess: () => {
      void inv()
      // Decisions touch estimates, appointments and the Tasks room too.
      void qc.invalidateQueries({ queryKey: ["estimates"] })
      void qc.invalidateQueries({ queryKey: ["appointments"] })
      void qc.invalidateQueries({ queryKey: ["tasks"] })
      window.setTimeout(() => { void inv() }, 2500)
    },
  })
}
export function useBulkMessage(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (v: { contactIds: string[]; body: string }) => ghlApi.bulkMessage(teamId!, v), onSuccess: inv })
}

export function useMarkRead(teamId: TeamId) {
  const inv = useInvalidateGhl()
  return useMutation({ mutationFn: (conversationId: string) => ghlApi.markRead(teamId!, conversationId), onSuccess: inv })
}
